import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileText, Images, Loader2, RotateCw, ShieldCheck, Trash2, X, XCircle, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import "../kyc-theme.css";

const FILE_LABELS = {
  document_front: "Fronte documento",
  document_back: "Retro documento",
  selfie_front: "Selfie fronte",
  selfie_right: "Selfie destra",
  selfie_left: "Selfie sinistra",
};

// motivi di rifiuto rapidi: etichetta chip id.preset.<k>, testo inserito id.preset.<k>Text
const REJECT_PRESETS = ["blurry", "expired", "backlight", "cut"];

const VERDICT_STYLE = {
  ok: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  attention: "bg-amber-50 text-amber-800 border border-amber-300",
  redo: "bg-rose-50 text-rose-700 border border-rose-200",
};

export function AdminIdentity() {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [countries, setCountries] = useState("IT");
  const [retention, setRetention] = useState(360);
  const [deadlineDays, setDeadlineDays] = useState(7);
  const [blurTh, setBlurTh] = useState(55);
  const [minW, setMinW] = useState(480);
  const [minH, setMinH] = useState(320);
  const [busy, setBusy] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);   // session_id con pannello rifiuto aperto
  const [rejectNote, setRejectNote] = useState("");
  const [redoSel, setRedoSel] = useState([]);          // passaggi da far rifare
  const [redoNotes, setRedoNotes] = useState({});       // motivo specifico per passaggio
  const [lightbox, setLightbox] = useState(null);      // {url, label, pdf} anteprima popup
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [inviteAllBusy, setInviteAllBusy] = useState(false);

  const openLightbox = (lb) => { setLightbox(lb); setZoom(1); setRotation(0); };
  const closeLightbox = () => {
    if (lightbox?.pdf) URL.revokeObjectURL(lightbox.url);
    if (lightbox?.prevUrl) URL.revokeObjectURL(lightbox.prevUrl);
    setLightbox(null);
  };

  const archiveSession = async (session) => {
    if (!window.confirm(t("id.archiveConfirm"))) return;
    setReviewBusy(session.session_id);
    try {
      await api.post(`/admin/identity/${session.session_id}/archive`, {});
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setReviewBusy(null);
    }
  };

  const inviteAll = async () => {
    if (!candidates?.length) return;
    if (!window.confirm(t("id.inviteAllConfirm").replace("{n}", candidates.length))) return;
    setInviteAllBusy(true);
    try {
      const { data } = await api.post("/admin/identity/invite-all", { channel: "email" });
      toast.success(t("id.inviteAllDone").replace("{sent}", data.sent).replace("{skipped}", data.skipped));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setInviteAllBusy(false);
    }
  };

  const italyManual = useMemo(() => countries.split(",").map((c) => c.trim().toUpperCase()).includes("IT"), [countries]);

  const load = useCallback(async () => {
    const [subs, settings, inviteCandidates] = await Promise.all([
      api.get(`/admin/identity/submissions${statusFilter ? `?status=${statusFilter}` : ""}`),
      api.get("/admin/kyc/settings"),
      api.get("/admin/identity/invite-candidates"),
    ]);
    setItems(subs.data.data || []);
    setMeta(subs.data.meta || null);
    setCandidates(inviteCandidates.data.data || []);
    setCountries(settings.data.data.identity_manual_countries || "IT");
    setRetention(settings.data.data.identity_retention_days || 360);
    setDeadlineDays(settings.data.data.identity_redo_deadline_days ?? 7);
    setBlurTh(settings.data.data.identity_blur_threshold ?? 55);
    setMinW(settings.data.data.identity_min_width ?? 480);
    setMinH(settings.data.data.identity_min_height ?? 320);
  }, [statusFilter]);

  useEffect(() => { load().catch(() => { setItems([]); setMeta(null); }); }, [load]);

  const toggleItaly = () => {
    const list = countries.split(",").map((c) => c.trim().toUpperCase()).filter(Boolean);
    setCountries((italyManual ? list.filter((c) => c !== "IT") : [...new Set([...list, "IT"])]).join(", "));
  };

  const fmtMinutes = (minutes) => {
    if (minutes == null) return "—";
    if (minutes < 60) return `${Math.round(minutes)} min`;
    return `${(minutes / 60).toFixed(1)} h`;
  };

  const fmtDate = (value) => {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const saveRules = async () => {
    setBusy(true);
    try {
      await api.post("/admin/kyc/settings", {
        identity_manual_countries: countries,
        identity_retention_days: Number(retention),
        identity_redo_deadline_days: Number(deadlineDays),
        identity_blur_threshold: Number(blurTh),
        identity_min_width: Number(minW),
        identity_min_height: Number(minH),
      });
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const review = async (session, action) => {
    setReviewBusy(session.session_id);
    try {
      await api.post(`/admin/identity/${session.session_id}/review`, { action, note: "" });
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setReviewBusy(null);
    }
  };

  // Rifiuto guidato: apre il pannello con motivo + checkbox dei passaggi da rifare.
  // Parte VUOTO (o con i soli passaggi segnalati dalla valutazione auto): l'admin
  // clicca i documenti da rifiutare — mai preselezionare tutto, altrimenti un clic
  // deseleziona e si rifiuta l'opposto di ciò che si voleva.
  const openReject = (session) => {
    const present = Object.keys(session.files || {});
    const flagged = [...new Set((session.auto_review?.issues || []).map((i) => i.kind))].filter((k) => present.includes(k));
    setRejecting(session.session_id);
    setRejectNote("");
    setRedoNotes({});
    setRedoSel(flagged);
  };

  const confirmReject = async (session) => {
    setReviewBusy(session.session_id);
    try {
      await api.post(`/admin/identity/${session.session_id}/review`, {
        action: "reject", note: rejectNote, redo_kinds: redoSel, redo_notes: redoNotes,
      });
      toast.success(t("admin.saved"));
      setRejecting(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setReviewBusy(null);
    }
  };

  const sendInvite = async (candidate, channel) => {
    setReviewBusy(`invite-${candidate.user_id}-${channel}`);
    try {
      await api.post("/admin/identity/invite", { user_id: candidate.user_id, channel });
      toast.success(t("id.inviteSent"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setReviewBusy(null);
    }
  };

  return (
    <div data-testid="admin-identity-panel" className="kyc-scope p-5 sm:p-7 space-y-8">
      <section data-testid="admin-kyc-destination-rules-tab" className="rounded-3xl border border-slate-200 bg-white p-6">
        <h3 className="font-display font-bold text-xl text-slate-900">{t("id.adminRulesTitle")}</h3>
        <p className="mt-1 text-sm text-slate-500">{t("id.adminRulesSub")}</p>
        <div className="mt-5 grid lg:grid-cols-[1fr_150px_150px_auto] gap-4 items-end">
          <div>
            <label className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("id.adminCountries")}</label>
            <input data-testid="admin-identity-countries-input" value={countries} onChange={(e) => setCountries(e.target.value.toUpperCase())}
              placeholder="IT, FR, ES" className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-mono-t text-sm uppercase outline-none focus:border-[#FF6B35]" />
          </div>
          <div>
            <label className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("id.adminRetention")}</label>
            <input data-testid="admin-kyc-retention-days-input" type="number" min="30" max="730" value={retention} onChange={(e) => setRetention(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF6B35]" />
          </div>
          <div>
            <label className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("id.deadlineDays")}</label>
            <input data-testid="admin-kyc-redo-deadline-days-input" type="number" min="1" max="30" value={deadlineDays} onChange={(e) => setDeadlineDays(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF6B35]" />
          </div>
          <button data-testid="admin-kyc-retention-save-button" onClick={saveRules} disabled={busy}
            className="rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
          </button>
        </div>
        <p className="mt-3 text-xs text-slate-400">{t("id.deadlineHint")}</p>
        <button type="button" data-testid="admin-rule-italy-manual-switch" onClick={toggleItaly}
          className={`mt-4 rounded-full px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${italyManual ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-slate-100 text-slate-500"}`}>
          {t("id.adminItalyManual")}: {italyManual ? "ON" : "OFF"}
        </button>
        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("id.qTitle")}</div>
          <p className="mt-1 text-xs text-slate-500">{t("id.qHint")}</p>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div>
              <label className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.qBlur")}</label>
              <input data-testid="admin-quality-blur-input" type="number" min="5" max="100000" step="5" value={blurTh} onChange={(e) => setBlurTh(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35]" />
            </div>
            <div>
              <label className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.qMinW")}</label>
              <input data-testid="admin-quality-minw-input" type="number" min="100" max="2000" step="10" value={minW} onChange={(e) => setMinW(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35]" />
            </div>
            <div>
              <label className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.qMinH")}</label>
              <input data-testid="admin-quality-minh-input" type="number" min="100" max="2000" step="10" value={minH} onChange={(e) => setMinH(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#FF6B35]" />
            </div>
          </div>
        </div>
      </section>

      <section data-testid="identity-invite-candidates" className="rounded-3xl border border-slate-200 bg-white p-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-xl text-slate-900">{t("id.inviteTitle")}</h3>
            <p className="mt-1 text-sm text-slate-500">{t("id.inviteSub")}</p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="identity-invite-count" className="rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest">
              {(candidates || []).length} {t("id.inviteCount")}
            </span>
            {(candidates || []).length > 0 && (
              <button type="button" data-testid="identity-invite-all-button" onClick={inviteAll} disabled={inviteAllBusy}
                className="inline-flex items-center gap-2 rounded-full bg-[#1E293B] text-white px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-50">
                {inviteAllBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {t("id.inviteAll")}
              </button>
            )}
          </div>
        </div>
        {candidates && candidates.length > 0 && (
          <div data-testid="identity-invite-candidate-list" className="mt-5 divide-y divide-slate-100">
            {candidates.slice(0, 8).map((candidate) => (
              <div key={candidate.user_id} data-testid={`identity-invite-candidate-${candidate.user_id}`}
                className="py-4 flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold text-slate-900 truncate">{candidate.name || candidate.email}</div>
                  <div className="mt-1 text-xs text-slate-500 truncate">
                    {candidate.email} · {candidate.orders_count} ordini · {(candidate.destinations || []).join(", ")}
                  </div>
                  {candidate.last_invite_at && (
                    <div className="mt-1 text-[11px] text-slate-400">{t("id.inviteLast")}: {new Date(candidate.last_invite_at).toLocaleString()}</div>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button type="button" data-testid={`identity-invite-email-${candidate.user_id}`}
                    onClick={() => sendInvite(candidate, "email")} disabled={reviewBusy === `invite-${candidate.user_id}-email`}
                    className="rounded-full bg-[#FF6B35] text-white px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-50">
                    {t("id.inviteEmail")}
                  </button>
                  <button type="button" data-testid={`identity-invite-sms-${candidate.user_id}`}
                    onClick={() => sendInvite(candidate, "sms")}
                    disabled={!candidate.phone_number || reviewBusy === `invite-${candidate.user_id}-sms`}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors disabled:opacity-40">
                    {t("id.inviteSms")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        {meta && (
          <div data-testid="admin-identity-report" className="mb-5 grid sm:grid-cols-3 gap-3">
            <div className="rounded-3xl border border-slate-200 bg-white p-4">
              <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.reportAvgReview")}</div>
              <div data-testid="identity-avg-review-time" className="mt-2 font-display font-black text-2xl text-slate-900">{fmtMinutes(meta.avg_review_minutes)}</div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-4">
              <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.reportAvgWait")}</div>
              <div data-testid="identity-avg-wait-time" className="mt-2 font-display font-black text-2xl text-slate-900">{fmtMinutes(meta.avg_wait_minutes)}</div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-4">
              <div className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("id.reportPending")}</div>
              <div data-testid="identity-pending-count" className="mt-2 font-display font-black text-2xl text-slate-900">{meta.counts?.submitted || 0}</div>
            </div>
          </div>
        )}
        <div className="mb-4 flex flex-wrap gap-2">
          {["", "draft", "submitted", "approved", "rejected", "expired"].map((value) => (
            <button key={value || "all"} data-testid={`identity-filter-${value || "all"}`} onClick={() => setStatusFilter(value)}
              className={`rounded-full px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest transition-colors ${
                statusFilter === value ? "bg-[#1E293B] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35]"}`}>
              {value ? t(`id.status.${value}`) : t("blog.all")} {value && meta?.counts?.[value] != null ? `(${meta.counts[value]})` : ""}
            </button>
          ))}
        </div>
        <h3 className="font-display font-bold text-xl text-slate-900">{t("id.adminQueueTitle")}</h3>
        <p className="mt-1 text-sm text-slate-500">{t("id.adminQueueSub")}</p>
        {items === null ? (
          <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : items.length === 0 ? (
          <div data-testid="admin-identity-empty" className="mt-4 rounded-3xl border border-dashed border-slate-300 py-14 text-center text-sm text-slate-400">{t("admin.noLogs")}</div>
        ) : (
          <div className="mt-4 space-y-4">
            {items.map((s) => (
              <article key={s.session_id} data-testid={`admin-identity-session-${s.session_id}`}
                className="rounded-3xl border border-slate-200 bg-white p-5">
                <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-[#0D9488]" />
                      <span className="font-display font-bold text-slate-900">{s.user?.email}</span>
                      <span data-testid={`admin-identity-status-${s.session_id}`} className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
                        s.status === "approved" ? "bg-emerald-50 text-emerald-700" : s.status === "rejected" ? "bg-rose-50 text-rose-700" :
                        s.status === "expired" ? "bg-slate-200 text-slate-600" : "bg-amber-50 text-amber-800"}`}>
                        {t(`id.status.${s.status}`)}
                      </span>
                      {s.user?.identity_blocked && (
                        <span data-testid={`admin-identity-blocked-${s.session_id}`}
                          className="rounded-full bg-slate-900 px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest text-white">
                          {t("id.userBlocked")}
                        </span>
                      )}
                      {s.auto_review && (
                        <span data-testid={`admin-auto-review-${s.session_id}`}
                          className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${VERDICT_STYLE[s.auto_review.verdict] || VERDICT_STYLE.attention}`}>
                          {t(`id.auto${s.auto_review.verdict === "ok" ? "Ok" : s.auto_review.verdict === "redo" ? "Redo" : "Attention"}`)}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 font-mono-t text-[10px] uppercase tracking-widest text-slate-400">
                      {fmtDate(s.submitted_at || s.created_at)} · {Object.keys(s.files || {}).length}/5 file
                    </div>
                    {s.status === "rejected" && s.redo_deadline_at && (
                      <div data-testid={`admin-identity-deadline-${s.session_id}`} className="mt-1 font-mono-t text-[10px] font-bold uppercase tracking-widest text-rose-500">
                        {t("id.deadlineAdmin").replace("{date}", new Date(s.redo_deadline_at).toLocaleDateString())}
                      </div>
                    )}
                    {s.review_note && <p className="mt-2 text-sm text-rose-600">{s.review_note}</p>}
                    <div className="mt-4 flex flex-wrap gap-3">
                      {Object.values(s.files || {}).map((f) => {
                        const issue = (s.auto_review?.issues || []).find((i) => i.kind === f.kind);
                        const issueKey = issue ? `id.issue${{ blurry: "Blurry", low_res: "LowRes", dark: "Dark" }[issue.reason] || "Missing"}` : null;
                        const needsRedo = s.status === "rejected" && (s.redo_kinds || []).includes(f.kind);
                        const isNew = (s.new_kinds || []).includes(f.kind);
                        const fileOk = s.status === "rejected" && !needsRedo;
                        // semaforo in coda: la miniatura "Nuovo" si colora in base all'esito del confronto
                        const oldS = f.prev_quality?.score;
                        const newS = f.quality?.score;
                        const cmpVerdict = isNew && oldS != null && newS != null
                          ? (newS >= oldS * 1.1 ? "sharper" : newS <= oldS * 0.9 ? "worse" : "similar") : null;
                        return (
                          <FileThumb key={f.file_id} file={f} issueKey={issueKey} redo={needsRedo} isNew={isNew} approved={fileOk} cmp={cmpVerdict} label={FILE_LABELS[f.kind] || f.kind}
                            t={t} onOpen={openLightbox} />
                        );
                      })}
                    </div>
                    {rejecting === s.session_id && (
                      <div data-testid={`admin-reject-panel-${s.session_id}`} className="mt-4 max-w-xl rounded-2xl border border-rose-200 bg-rose-50/60 p-4 space-y-3">
                        <input data-testid="admin-reject-note-input" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
                          placeholder={t("id.rejectNotePlaceholder")}
                          className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-rose-400" />
                        <div>
                          <div className="mb-2 font-mono-t text-[9px] font-bold uppercase tracking-widest text-rose-500">{t("id.redoTitle")}</div>
                          <div className="flex flex-wrap gap-2">
                            {Object.keys(s.files || {}).map((kind) => {
                              const on = redoSel.includes(kind);
                              return (
                                <button key={kind} type="button" data-testid={`admin-redo-kind-${kind}`}
                                  onClick={() => setRedoSel(on ? redoSel.filter((k) => k !== kind) : [...redoSel, kind])}
                                  className={`rounded-full px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest transition-colors ${
                                    on ? "bg-rose-600 text-white" : "border border-slate-200 bg-white text-slate-500"}`}>
                                  {FILE_LABELS[kind] || kind}
                                </button>
                              );
                            })}
                          </div>
                          {redoSel.length > 0 && (
                            <div className="mt-2 space-y-3">
                              {redoSel.map((kind) => (
                                <div key={kind}>
                                  <input data-testid={`admin-redo-note-${kind}`} value={redoNotes[kind] || ""}
                                    onChange={(e) => setRedoNotes({ ...redoNotes, [kind]: e.target.value })}
                                    placeholder={`${FILE_LABELS[kind] || kind}: ${t("id.rejectNotePlaceholder")}`}
                                    className="w-full rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm outline-none focus:border-rose-400" />
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {REJECT_PRESETS.map((p) => (
                                      <button key={p} type="button" data-testid={`admin-redo-preset-${kind}-${p}`}
                                        onClick={() => setRedoNotes({ ...redoNotes, [kind]: t(`id.preset.${p}Text`) })}
                                        className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 font-mono-t text-[8px] font-bold uppercase tracking-widest text-rose-600 hover:bg-rose-100 transition-colors">
                                        {t(`id.preset.${p}`)}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 border-t border-rose-200 pt-3">
                          <button type="button" data-testid="admin-reject-confirm-button" onClick={() => confirmReject(s)}
                            disabled={reviewBusy === s.session_id || redoSel.length === 0}
                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-rose-600 px-6 py-3 font-mono-t text-xs font-bold uppercase tracking-widest text-white shadow-md shadow-rose-600/25 hover:bg-rose-700 transition-colors disabled:opacity-50">
                            {reviewBusy === s.session_id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                              <><XCircle className="w-4 h-4" /> {t("id.rejectConfirm")} · {redoSel.length}/5</>
                            )}
                          </button>
                          <button type="button" data-testid="admin-reject-cancel-button" onClick={() => setRejecting(null)}
                            className="rounded-2xl border border-slate-200 bg-white px-5 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-slate-300 transition-colors">
                            {t("id.cancelBtn")}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`admin-identity-approve-${s.session_id}`} onClick={() => review(s, "approve")}
                      disabled={reviewBusy === s.session_id || s.status === "approved"}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-50">
                      {reviewBusy === s.session_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {t("id.adminApprove")}
                    </button>
                    <button data-testid={`admin-identity-reject-${s.session_id}`} onClick={() => (rejecting === s.session_id ? setRejecting(null) : openReject(s))}
                      disabled={reviewBusy === s.session_id || s.status === "rejected"}
                      className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-rose-700 transition-colors disabled:opacity-50">
                      <XCircle className="w-4 h-4" /> {t("id.adminReject")}
                    </button>
                    {(s.status === "rejected" || s.status === "expired") && (
                      <button data-testid={`admin-identity-archive-${s.session_id}`} onClick={() => archiveSession(s)}
                        disabled={reviewBusy === s.session_id}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-slate-500 px-5 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:border-rose-400 hover:text-rose-600 transition-colors disabled:opacity-50">
                        <Trash2 className="w-4 h-4" /> {t("id.archive")}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {lightbox && (
        <div data-testid="admin-identity-lightbox" onClick={closeLightbox}
          className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className={`w-full ${lightbox.compare ? "max-w-6xl" : "max-w-3xl"}`} onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-3 text-white">
              <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.22em]">{lightbox.label}</span>
              <div className="flex items-center gap-2">
                {!lightbox.pdf && (
                  <>
                    <button type="button" data-testid="admin-lightbox-zoom-out" aria-label="Zoom out"
                      onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.5) * 10) / 10))}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white hover:bg-white transition-colors">
                      <ZoomOut className="w-5 h-5" />
                    </button>
                    <span data-testid="admin-lightbox-zoom-level" className="w-12 text-center font-mono-t text-[10px] font-bold">{Math.round(zoom * 100)}%</span>
                    <button type="button" data-testid="admin-lightbox-zoom-in" aria-label="Zoom in"
                      onClick={() => setZoom((z) => Math.min(4, Math.round((z + 0.5) * 10) / 10))}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white hover:bg-white transition-colors">
                      <ZoomIn className="w-5 h-5" />
                    </button>
                    <button type="button" data-testid="admin-lightbox-rotate" aria-label="Rotate"
                      onClick={() => setRotation((r) => (r + 90) % 360)}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white hover:bg-white transition-colors">
                      <RotateCw className="w-5 h-5" />
                    </button>
                  </>
                )}
                <button type="button" data-testid="admin-lightbox-close" aria-label={t("auto.close")}
                  onClick={closeLightbox}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white hover:bg-white transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {lightbox.compare ? (
              <div data-testid="admin-lightbox-compare">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[["prevUrl", t("id.beforeTag"), lightbox.prevQ], ["url", t("id.afterTag"), lightbox.newQ]].map(([key, tag, q]) => (
                    <figure key={key} className="relative">
                      <span className={`absolute left-2 top-2 z-10 rounded-md px-2 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${
                        key === "prevUrl" ? "bg-slate-700/85 text-white" : "bg-emerald-600/90 text-white"}`}>
                        {tag}
                      </span>
                      {q?.score != null && (
                        <span data-testid={`admin-compare-score-${key}`}
                          className={`absolute bottom-2 left-2 z-10 rounded-md px-2 py-0.5 font-mono-t text-[10px] font-bold ${
                            key === "prevUrl" ? "bg-slate-900/80 text-slate-300" : "bg-slate-900/80 text-emerald-300"}`}>
                          {t("id.cmpScore")}: {Math.round(q.score)}
                        </span>
                      )}
                      <div className="flex max-h-[70vh] items-center justify-center overflow-auto rounded-2xl bg-slate-900/40">
                        <img src={lightbox[key]} alt={`${lightbox.label} — ${tag}`}
                          style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: "transform 0.2s ease" }}
                          className="max-h-[70vh] max-w-full rounded-2xl shadow-2xl" />
                      </div>
                    </figure>
                  ))}
                </div>
                <CompareVerdict prevQ={lightbox.prevQ} newQ={lightbox.newQ} t={t} />
              </div>
            ) : lightbox.pdf ? (
              <iframe src={lightbox.url} title={lightbox.label} className="h-[75vh] w-full rounded-2xl bg-white" />
            ) : (
              <div data-testid="admin-lightbox-viewport" className="flex max-h-[75vh] items-center justify-center overflow-auto rounded-2xl">
                <img src={lightbox.url} alt={lightbox.label}
                  style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, transition: "transform 0.2s ease" }}
                  className="max-h-[75vh] max-w-full rounded-2xl shadow-2xl" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Miniatura documento: caricata via API autenticata (blob → object URL). Click = popup
// nella stessa pagina (immagini subito, PDF caricati al click e mostrati in iframe).
function FileThumb({ file, issueKey, redo = false, isNew = false, approved = false, cmp = null, label, t, onOpen }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const isImage = (file.content_type || "").startsWith("image/");

  useEffect(() => {
    if (!isImage) return undefined;
    let alive = true;
    let objUrl = null;
    api.get(`/admin/identity/files/${file.file_id}`, { responseType: "blob" })
      .then(({ data }) => { objUrl = URL.createObjectURL(data); if (alive) setUrl(objUrl); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [file.file_id, isImage]);

  const open = async () => {
    if (isImage && url) {
      // confronto prima/dopo: se il passaggio è stato ricaricato, apre la versione
      // precedente accanto a quella nuova (solo se anche la vecchia è un'immagine)
      if (file.prev_file_id) {
        try {
          const { data } = await api.get(`/admin/identity/files/${file.prev_file_id}`, { responseType: "blob" });
          if ((data.type || "").startsWith("image/")) {
            onOpen({ url, prevUrl: URL.createObjectURL(data), label, pdf: false, compare: true,
                     newQ: file.quality || null, prevQ: file.prev_quality || null });
            return;
          }
        } catch { /* vecchia versione non accessibile: mostra solo la nuova */ }
      }
      onOpen({ url, label, pdf: false });
      return;
    }
    try {
      const { data } = await api.get(`/admin/identity/files/${file.file_id}`, { responseType: "blob" });
      onOpen({ url: URL.createObjectURL(data), label, pdf: !isImage });
    } catch {
      toast.error(t("misc.error"));
    }
  };

  return (
    <button type="button" data-testid={`admin-identity-file-${file.kind}`} onClick={open}
      title={issueKey ? t(issueKey) : label} className="group w-28 shrink-0 text-left">
      <div className={`relative h-20 w-28 overflow-hidden rounded-xl border-2 transition-colors ${
        redo ? "border-rose-500 bg-rose-50 ring-2 ring-rose-200" :
        issueKey ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50 group-hover:border-[#FF6B35]"}`}>
        {isImage && url ? (
          <img src={url} alt={label} className={`h-full w-full object-cover ${redo ? "opacity-70" : ""}`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {isImage && !failed ? <Loader2 className="w-4 h-4 animate-spin text-slate-300" /> : <FileText className="w-5 h-5 text-slate-400" />}
          </div>
        )}
        {redo && (
          <span data-testid={`admin-identity-redo-${file.kind}`}
            className="absolute left-1 top-1 rounded-md bg-rose-600 px-1.5 py-0.5 font-mono-t text-[8px] font-bold uppercase tracking-widest text-white">
            {t("id.redoTag")}
          </span>
        )}
        {isNew && !redo && (
          <span data-testid={`admin-identity-new-${file.kind}`}
            title={cmp ? `${t("id.cmpScore")} ${Math.round(file.prev_quality.score)} → ${Math.round(file.quality.score)}` : undefined}
            className={`absolute right-1 top-1 rounded-md px-1.5 py-0.5 font-mono-t text-[8px] font-bold uppercase tracking-widest text-white ${
              cmp === "worse" ? "bg-rose-600" : cmp === "similar" ? "bg-amber-500" : "bg-emerald-600"}`}>
            {t("id.newTag")}{cmp === "worse" ? " ↓" : cmp === "sharper" ? " ↑" : ""}
          </span>
        )}
        {approved && !isNew && (
          <span data-testid={`admin-identity-ok-${file.kind}`}
            className="absolute right-1 top-1 rounded-md bg-emerald-100 px-1.5 py-0.5 font-mono-t text-[8px] font-bold uppercase tracking-widest text-emerald-700">
            {t("id.okTag")}
          </span>
        )}
        {issueKey && !redo && (
          <span className="absolute right-1 bottom-1 rounded-full bg-amber-400 p-0.5 text-white">
            <AlertTriangle className="w-3 h-3" />
          </span>
        )}
        {file.prev_file_id && !issueKey && (
          <span data-testid={`admin-identity-compare-hint-${file.kind}`} title={`${t("id.beforeTag")} / ${t("id.afterTag")}`}
            className="absolute bottom-1 right-1 rounded-full bg-slate-900/75 p-0.5 text-white">
            <Images className="w-3 h-3" />
          </span>
        )}
        {file.quality?.score != null && (
          <span data-testid={`admin-identity-score-${file.kind}`}
            title={`${file.quality.width || "?"}x${file.quality.height || "?"}px`}
            className={`absolute bottom-1 left-1 rounded-md px-1.5 py-0.5 font-mono-t text-[8px] font-bold ${
              issueKey ? "bg-amber-400/95 text-white" : "bg-slate-900/75 text-emerald-300"}`}>
            {Math.round(file.quality.score)}
          </span>
        )}
      </div>
      <div className="mt-1 truncate font-mono-t text-[8px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    </button>
  );
}

// Verdetto automatico sul confronto prima/dopo: la foto nuova è più nitida della
// vecchia? Soglie ±10% sul punteggio di nitidezza (varianza Laplaciana).
function CompareVerdict({ prevQ, newQ, t }) {
  const oldS = prevQ?.score;
  const newS = newQ?.score;
  if (oldS == null || newS == null) return null;
  const verdict = newS >= oldS * 1.1 ? "Sharper" : newS <= oldS * 0.9 ? "Worse" : "Similar";
  const style = verdict === "Sharper" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
    : verdict === "Worse" ? "border-rose-300 bg-rose-50 text-rose-700"
    : "border-amber-300 bg-amber-50 text-amber-800";
  return (
    <div data-testid="admin-compare-verdict"
      className={`mt-3 flex items-center justify-center gap-2 rounded-2xl border px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest ${style}`}>
      {t(`id.cmp${verdict}`)} · {t("id.cmpScore")} {Math.round(oldS)} → {Math.round(newS)}
    </div>
  );
}
